import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { BulkProposalCard } from './BulkProposalCard';
import type { MoveProcurementPayload } from './proposal-part.types';
import { useProcurementRowStatus } from './use-procurement-row-status';

interface Props {
  proposalId: string;
  payload: MoveProcurementPayload;
  /** Which document kind the card is about — decides the title wording. */
  kind: 'requisitions' | 'purchaseOrders';
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/**
 * Confirmation card for a bulk requisition or purchase-order approval change. Every document
 * is listed with the state it shows now, so the user sees exactly what the move affects.
 */
export function MoveProcurementStatusProposalCard({
  proposalId,
  payload,
  kind,
  resolved,
  onConfirm,
  onCancel
}: Props) {
  const t = useFeatureTranslation('aiChat');
  const rowStatusOf = useProcurementRowStatus(kind);
  const tApprovalStatus = useEnumTranslation('approvalStatus');
  const status = payload.targetApprovalStatus ? tApprovalStatus(payload.targetApprovalStatus) : '';

  return (
    <BulkProposalCard
      title={t(`proposals.move-procurement.${kind}.title`, { status })}
      description={t(`proposals.move-procurement.${kind}.countDescription`, {
        count: payload.documents.length,
        status
      })}
      proposalId={proposalId}
      rows={payload.documents}
      rowKey={row => row.id}
      primary={row => <span className='font-medium'>{row.number}</span>}
      secondary={row => (
        <span className='text-foreground-muted'>{rowStatusOf(row)?.label ?? ''}</span>
      )}
      resolved={resolved}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
